/* tslint:disable */
/* eslint-disable */
/**
 * Business Orchestration Layer (BOL) API
 * Business Orchestration layer (BOL) API. Public part, exposed to BOL client applications.  Glossary | Definition -- | -- <strong>Logistics Object</strong> | A resource in the BOL system that represents a business entity, such as a QuotationRequest, Quotation, or Service. Logistics Objects are managed through the BOL API and can be accessed or modified by Users based on their permissions i.e. based on their AD Entra ID roles and ACL (Access Control List) modes. <strong>Member</strong> | A data owner in the BOL system, uniquely identified by its `memberIdentifier`. Members are used to manage access control and ownership of Logistics Objects. <strong>User</strong> | A person or entity that interacts with the BOL system, typically through a client application, and beeing authenticated. Users are associated with Members and can perform actions on Logistics Objects based on their access level. User access token includes the Member identifier as part of roles claims. Each path in the API is protected by a custom role defined in AD Entra ID, and operations on Logistics Objects are protected by specific Access modes defined by members. <strong>Access Control</strong> | The mechanism by which the BOL system restricts access to API paths and resources based on the identity of the User and their associated Member. Access control is enforced through AD Entra ID roles and ACL (Access Control List) modes defined for every single Logistics Object. <strong>ACL (Access Control List)</strong> | A set of authorizations that define what actions a User can perform on a Logistics Object. The level of access granted to Users is determined by ACL modes defined in authorizations for specific Logistics Objects. ACL Modes are not directly associated with Users but rather with Members, which are the data owners of Logistics Objects. Each Logistics Object has an ACL that specifies the access modes for different Members. Members need to exist in the BOL system to be granted access to Logistics Objects. <strong>ACL Mode</strong> | The specific level of access control applied to a Logistics Object, determining what actions can be performed by Users. ACL modes are defined for each Logistics Object by `memberIdentifier` and include `READ`, `WRITE`, and `CONTROL`. Each mode includes access granted by the modes under it, so `CONTROL` includes `WRITE` and `READ`, and `WRITE` includes `READ`. <strong>Change Request</strong> | A request to modify a Logistics Object, which is processed by the BOL system. Change requests are created when a User wants to update an existing Logistics Object, and they are subject to approval and application by the system. All changes to Logistics Objects are done through change requests. This allows for a controlled and auditable process of modifications. <strong>Host</strong> | A server alias in the BOL system, used to abstract URI values internally. This allows for flexibility in the underlying infrastructure when migrating or changing server configurations. 
 *
 * The version of the OpenAPI document: 7.2.1-develop.2
 * Contact: benoit.tiers@logist-x.eu
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { SpecialInstruction } from './SpecialInstruction';
import {
    SpecialInstructionFromJSON,
    SpecialInstructionFromJSONTyped,
    SpecialInstructionToJSON,
    SpecialInstructionToJSONTyped,
} from './SpecialInstruction';
import type { SpecialCharacteristic } from './SpecialCharacteristic';
import {
    SpecialCharacteristicFromJSON,
    SpecialCharacteristicFromJSONTyped,
    SpecialCharacteristicToJSON,
    SpecialCharacteristicToJSONTyped,
} from './SpecialCharacteristic';
import type { ExternalReference } from './ExternalReference';
import {
    ExternalReferenceFromJSON,
    ExternalReferenceFromJSONTyped,
    ExternalReferenceToJSON,
    ExternalReferenceToJSONTyped,
} from './ExternalReference';
import type { Value } from './Value';
import {
    ValueFromJSON,
    ValueFromJSONTyped,
    ValueToJSON,
    ValueToJSONTyped,
} from './Value';
import type { Country } from './Country';
import {
    CountryFromJSON,
    CountryFromJSONTyped,
    CountryToJSON,
    CountryToJSONTyped,
} from './Country';
import type { Dimensions } from './Dimensions';
import {
    DimensionsFromJSON,
    DimensionsFromJSONTyped,
    DimensionsToJSON,
    DimensionsToJSONTyped,
} from './Dimensions';
import type { Document } from './Document';
import {
    DocumentFromJSON,
    DocumentFromJSONTyped,
    DocumentToJSON,
    DocumentToJSONTyped,
} from './Document';
import type { Link } from './Link';
import {
    LinkFromJSON,
    LinkFromJSONTyped,
    LinkToJSON,
    LinkToJSONTyped,
} from './Link';
import type { Contact } from './Contact';
import {
    ContactFromJSON,
    ContactFromJSONTyped,
    ContactToJSON,
    ContactToJSONTyped,
} from './Contact';

/**
 * Response schema for Item logistics object.
 * @export
 * @interface ItemResponse
 */
export interface ItemResponse {
    /**
     * Unique identifier of the production batch of the product item. It is optional in many cases, but for some products, such as food, chemical products, and pharmaceutical products, it may be mandatory to ensure product traceability.
     * @type {string}
     * @memberof ItemResponse
     */
    batchNumber?: string;
    /**
     * Expiry date of the product item. The product item should not be used after this date.
     * @type {Date}
     * @memberof ItemResponse
     */
    productExpiryDate?: Date;
    /**
     * Date of the production batch of the item in UTC.
     * @type {Date}
     * @memberof ItemResponse
     */
    productionDate?: Date;
    /**
     * Unique ID of the item defined by the producer.
     * @type {string}
     * @memberof ItemResponse
     */
    serialNumber?: string;
    /**
     * List of contacts related to the item. These can be individuals or teams who can be contacted about the item. Rarely used if an item requires a contact.
     * @type {Array<Contact>}
     * @memberof ItemResponse
     */
    contacts?: Array<Contact>;
    /**
     * List of product items contained in another product item.
     * @type {Array<Link>}
     * @memberof ItemResponse
     */
    containedItems?: Array<Link>;
    /**
     * Sizes of the package of the item and its volume. If the dimensions are not really measurable, only volume is provided.
     * @type {Dimensions}
     * @memberof ItemResponse
     */
    dimensions?: Dimensions;
    /**
     * List of documents related to an item such as instructions and documents for dangerous goods.
     * @type {Array<Document>}
     * @memberof ItemResponse
     */
    documents?: Array<Document>;
    /**
     * External references for the item should be used if they are defined in a legacy system that registers the item under a different reference identifier than the URI. This is useful for searching for the item using the ID from the legacy app. Some common types of external references include 'SELLER_ID'.
     * @type {Array<ExternalReference>}
     * @memberof ItemResponse
     */
    externalReferences?: Array<ExternalReference>;
    /**
     * 
     * @type {Link}
     * @memberof ItemResponse
     */
    isInItem?: Link;
    /**
     * Country where the product item was produced.
     * @type {Country}
     * @memberof ItemResponse
     */
    productionCountry?: Country;
    /**
     * Products included in the item.
     * @type {Array<Link>}
     * @memberof ItemResponse
     */
    products?: Array<Link>;
    /**
     * Quantity of units contained in the item. For example, a box of Neils containing 1000 units.
     * @type {Value}
     * @memberof ItemResponse
     */
    quantity?: Value;
    /**
     * Special characteristics applied to the item and to be shared with a provider.
     * @type {Array<SpecialCharacteristic>}
     * @memberof ItemResponse
     */
    specialCharacteristics?: Array<SpecialCharacteristic>;
    /**
     * Special instructions to perform the delivery. They are defined between the seller and the buyer.
     * @type {Array<SpecialInstruction>}
     * @memberof ItemResponse
     */
    specialInstructions?: Array<SpecialInstruction>;
    /**
     * 
     * @type {Value}
     * @memberof ItemResponse
     */
    weight?: Value;
    /**
     * Internal unique identifier of the resource. Id can have different formats like UUID v4, UUID v7, or any other string calculated.
     * @type {string}
     * @memberof ItemResponse
     */
    id?: string;
}

/**
 * Check if a given object implements the ItemResponse interface.
 */
export function instanceOfItemResponse(value: object): value is ItemResponse {
    return true;
}

export function ItemResponseFromJSON(json: any): ItemResponse {
    return ItemResponseFromJSONTyped(json, false);
}

export function ItemResponseFromJSONTyped(json: any, ignoreDiscriminator: boolean): ItemResponse {
    if (json == null) {
        return json;
    }
    return {
        
        'batchNumber': json['batchNumber'] == null ? undefined : json['batchNumber'],
        'productExpiryDate': json['productExpiryDate'] == null ? undefined : (new Date(json['productExpiryDate'])),
        'productionDate': json['productionDate'] == null ? undefined : (new Date(json['productionDate'])),
        'serialNumber': json['serialNumber'] == null ? undefined : json['serialNumber'],
        'contacts': json['contacts'] == null ? undefined : ((json['contacts'] as Array<any>).map(ContactFromJSON)),
        'containedItems': json['containedItems'] == null ? undefined : ((json['containedItems'] as Array<any>).map(LinkFromJSON)),
        'dimensions': json['dimensions'] == null ? undefined : DimensionsFromJSON(json['dimensions']),
        'documents': json['documents'] == null ? undefined : ((json['documents'] as Array<any>).map(DocumentFromJSON)),
        'externalReferences': json['externalReferences'] == null ? undefined : ((json['externalReferences'] as Array<any>).map(ExternalReferenceFromJSON)),
        'isInItem': json['isInItem'] == null ? undefined : LinkFromJSON(json['isInItem']),
        'productionCountry': json['productionCountry'] == null ? undefined : CountryFromJSON(json['productionCountry']),
        'products': json['products'] == null ? undefined : ((json['products'] as Array<any>).map(LinkFromJSON)),
        'quantity': json['quantity'] == null ? undefined : ValueFromJSON(json['quantity']),
        'specialCharacteristics': json['specialCharacteristics'] == null ? undefined : ((json['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicFromJSON)),
        'specialInstructions': json['specialInstructions'] == null ? undefined : ((json['specialInstructions'] as Array<any>).map(SpecialInstructionFromJSON)),
        'weight': json['weight'] == null ? undefined : ValueFromJSON(json['weight']),
        'id': json['id'] == null ? undefined : json['id'],
    };
}

export function ItemResponseToJSON(json: any): ItemResponse {
    return ItemResponseToJSONTyped(json, false);
}

export function ItemResponseToJSONTyped(value?: ItemResponse | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'batchNumber': value['batchNumber'],
        'productExpiryDate': value['productExpiryDate'] == null ? value['productExpiryDate'] : value['productExpiryDate'].toISOString(),
        'productionDate': value['productionDate'] == null ? value['productionDate'] : value['productionDate'].toISOString(),
        'serialNumber': value['serialNumber'],
        'contacts': value['contacts'] == null ? undefined : ((value['contacts'] as Array<any>).map(ContactToJSON)),
        'containedItems': value['containedItems'] == null ? undefined : ((value['containedItems'] as Array<any>).map(LinkToJSON)),
        'dimensions': DimensionsToJSON(value['dimensions']),
        'documents': value['documents'] == null ? undefined : ((value['documents'] as Array<any>).map(DocumentToJSON)),
        'externalReferences': value['externalReferences'] == null ? undefined : ((value['externalReferences'] as Array<any>).map(ExternalReferenceToJSON)),
        'isInItem': LinkToJSON(value['isInItem']),
        'productionCountry': CountryToJSON(value['productionCountry']),
        'products': value['products'] == null ? undefined : ((value['products'] as Array<any>).map(LinkToJSON)),
        'quantity': ValueToJSON(value['quantity']),
        'specialCharacteristics': value['specialCharacteristics'] == null ? undefined : ((value['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicToJSON)),
        'specialInstructions': value['specialInstructions'] == null ? undefined : ((value['specialInstructions'] as Array<any>).map(SpecialInstructionToJSON)),
        'weight': ValueToJSON(value['weight']),
        'id': value['id'],
    };
}

